import { i18n } from "@lingui/core";
import dayjs from "dayjs";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { apiGetPreferredLanguage } from "../apiCalls";
import { getPersistStorage } from "@/utils/persist-storage";
import type { Locale } from "../../../shared/constants/locales";
import {
  DEFAULT_LOCALE,
  getBrowserPreferredLocale,
  getClosestSupportedLocale,
} from "@/utils/locale-utils";

const getClosestSupportedLanguage = (
  language: string | null | undefined,
): Locale => {
  return getClosestSupportedLocale(language) ?? DEFAULT_LOCALE;
};

const getPreferredLanguage = async (token: string) => {
  if (token) {
    const response = await apiGetPreferredLanguage(token);
    return response.data.preferredLanguage;
  }

  const primaryLanguage = getBrowserPreferredLocale();
  return {
    userPreferredLanguage: primaryLanguage,
    clientPreferredLanguage: primaryLanguage,
  };
};

type PreferredLanguage = {
  userPreferredLanguage: Locale;
  clientPreferredLanguage: Locale;
};

type PreferredLanguageStore = {
  preferredLanguage: PreferredLanguage;
  activateLocale: (language: string) => Promise<void>;
  setPreferredLanguage: (value: PreferredLanguage) => Promise<void>;
  loadPreferredLanguage: () => Promise<void>;
};

const getNormalizedPreferredLanguage = (
  value: Partial<PreferredLanguage>,
): PreferredLanguage => {
  const userPreferredLanguage = getClosestSupportedLanguage(
    value.userPreferredLanguage,
  );

  return {
    userPreferredLanguage,
    clientPreferredLanguage:
      getClosestSupportedLocale(value.clientPreferredLanguage) ??
      userPreferredLanguage,
  };
};

const initialPreferredLanguage = getNormalizedPreferredLanguage({
  userPreferredLanguage: getBrowserPreferredLocale(),
  clientPreferredLanguage: getBrowserPreferredLocale(),
});

const usePreferredLanguageStore = create<PreferredLanguageStore>()(
  persist(
    (set, get) => ({
      preferredLanguage: initialPreferredLanguage,

      activateLocale: async (language: string) => {
        const locale = getClosestSupportedLanguage(language);

        dayjs.locale(locale);
        document.documentElement.lang = locale;

        try {
          // Dynamically import the compiled messages file for the given language.
          const { messages } = await import(
            `../locales/${locale}/messages.mjs`
          );

          // Load and activate the locale in your i18n library.
          i18n.load(locale, messages);
          i18n.activate(locale);
        } catch (error) {
          console.error(`Failed to load locale messages for ${locale}:`, error);
        }
      },

      setPreferredLanguage: async (value) => {
        const preferredLanguage = getNormalizedPreferredLanguage(value);

        set({ preferredLanguage });
        await get().activateLocale(preferredLanguage.userPreferredLanguage);
      },

      loadPreferredLanguage: async () => {
        const token = localStorage.getItem("usr_token") || "";
        const preferredLanguage = getNormalizedPreferredLanguage(
          await getPreferredLanguage(token),
        );

        set({ preferredLanguage });
        await get().activateLocale(preferredLanguage.userPreferredLanguage);
      },
    }),
    {
      name: "lang",
      storage: createJSONStorage(getPersistStorage),
    },
  ),
);

export default usePreferredLanguageStore;
